import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Download, Loader2, Plus, Trash2, FilePlus, Pill, Check } from "lucide-react";
import { toast } from "sonner";
import { PHYSICIANS, TEMPLATES, RUBRIC_GROUPS } from "@/components/case_schema";

// ---------- Section definitions per template ----------
const HOMEO_SECTIONS = [
  { title: "3 · Past History", fields: [
    ["past_illnesses","Major past illnesses","ta"],["surgeries","Surgeries","ta"],
    ["accidents","Accidents / injuries","ta"],["drugs_used","Drugs / tonics used","ta"],
    ["vaccinations","Vaccinations & reactions","ta"]] },
  { title: "4 · Family History", fields: [
    ["family_diseases","Family diseases","ta"],["siblings","Siblings","ta"],
    ["spouse_health","Spouse","ta"],["children","Children","ta"]] },
  { title: "5 · Personal History", fields: [
    ["birth_history","Birth & pregnancy","ta"],["milestones","Milestones","ta"],
    ["menstrual_history","Menstrual","ta"],["obstetric_history","Obstetric","ta"],
    ["sexual_history","Sexual","ta"]] },
  { title: "6 · Systems Review", fields: [
    ["appetite_thirst","Appetite & Thirst","ta"],["food_prefs","Food likes/dislikes","ta"],
    ["stool","Stool","ta"],["urination","Urination","ta"],["sweat","Sweat","ta"],
    ["fever_chill","Fever/Chill","ta"],["chest","Chest","ta"],["head_face","Head/Face","ta"],
    ["mouth_throat","Mouth/Throat","ta"],["back_limbs","Back/Limbs","ta"],
    ["skin_hair","Skin/Hair","ta"],["sidedness","Sidedness","ta"]] },
  { title: "7 · Mind", fields: [
    ["mind_anxiety","Anxieties","ta"],["mind_fears","Fears","ta"],["mind_anger","Anger","ta"],
    ["mind_weeping","Weeping","ta"],["mind_grief","Grief","ta"],["mind_joys","Joys","ta"],
    ["mind_wishes","3 wishes","ta"],["mind_situation","Life situation","ta"],
    ["mind_thoughts","Unwanted thoughts","ta"]] },
  { title: "8 · Sleep & Dreams", fields: [
    ["sleep_posture","Posture","ta"],["sleep_quality","Quality","ta"],
    ["sleep_habits","Habits","ta"],["dreams","Dreams","ta"]] },
  { title: "9 · Physician's Notes", fields: [
    ["notes_constitutional","Constitutional impression","ta"],
    ["notes_miasm","Miasm","ta"],["notes_repertorisation","Repertorisation lead","ta"]] },
];

const ALLO_COMMON = [
  { title: "Past & Personal History", fields: [
    ["past_illnesses","Past medical history","ta"],["surgeries","Surgeries","ta"],
    ["drugs_used","Drug history / allergies","ta"],["family_diseases","Family history","ta"],
    ["personal_habits","Personal habits (tobacco/alcohol/diet)","ta"]] },
];

const ALLO_TEMPLATE_SECTIONS = {
  general: [{ title:"General Physical Exam", fields:[
    ["gen_vitals","Vitals (BP/PR/RR/Temp/SpO2)","ta"],
    ["gen_appearance","General appearance","ta"],
    ["gen_systemic","Systemic — CVS/RS/CNS/PA","ta"]] }],
  dermatology: [{ title:"Dermatological Examination", fields:[
    ["derm_site","Site & distribution","ta"],["derm_lesion_type","Type of lesion","ta"],
    ["derm_morphology","Number, size, shape, colour","ta"],
    ["derm_surface","Surface & margin","ta"],["derm_palpation","Palpation","ta"],
    ["derm_symptoms","Associated symptoms","ta"],
    ["derm_nail_hair","Nail/hair/mucosa involvement","ta"]] }],
  orthopedics: [{ title:"Orthopedic Examination", fields:[
    ["ortho_site","Site & side","ta"],["ortho_mechanism","Mechanism of injury","ta"],
    ["ortho_inspection","Inspection (swelling/deformity)","ta"],
    ["ortho_palpation","Palpation (tenderness/warmth)","ta"],
    ["ortho_rom","Range of motion","ta"],["ortho_power","Muscle power (MRC)","ta"],
    ["ortho_nv","Neurovascular status","ta"],["ortho_special_tests","Special tests","ta"],
    ["ortho_imaging","X-ray / MRI findings","ta"]] }],
  pediatrics: [{ title:"Pediatric Assessment", fields:[
    ["peds_birth","Birth weight / delivery","ta"],["peds_immunisation","Immunisation status","ta"],
    ["peds_feeding","Feeding history","ta"],["peds_milestones","Milestones","ta"],
    ["peds_growth","Growth (Ht/Wt/HC percentiles)","ta"],
    ["peds_systemic","Systemic examination","ta"]] }],
  gynecology: [{ title:"Gynecological Assessment", fields:[
    ["gyn_menstrual","Menstrual history","ta"],["gyn_obstetric","Obstetric history","ta"],
    ["gyn_contraception","Contraception","ta"],["gyn_sexual","Sexual & marital","ta"],
    ["gyn_findings","Pap/USG/hormonal findings","ta"],["gyn_exam","P/A · P/V exam","ta"]] }],
  ent: [{ title:"ENT Examination", fields:[
    ["ent_ear","Ear — external, TM, hearing","ta"],
    ["ent_nose","Nose — septum, mucosa, discharge","ta"],
    ["ent_throat","Throat — tonsils, pharynx, larynx","ta"],
    ["ent_neck","Neck — nodes, thyroid","ta"],
    ["ent_investigations","Audiometry / endoscopy","ta"]] }],
  ophthalmology: [{ title:"Ophthalmology Examination", fields:[
    ["eye_va","Visual acuity (R/L)"],["eye_refraction","Refraction"],
    ["eye_anterior","Anterior segment","ta"],["eye_iop","IOP"],
    ["eye_fundus","Fundus","ta"],["eye_investigations","Investigations","ta"]] }],
  psychiatry: [{ title:"Mental Status Examination", fields:[
    ["psy_appearance","Appearance & behaviour","ta"],["psy_speech","Speech","ta"],
    ["psy_mood","Mood & affect","ta"],["psy_thought","Thought — form/content","ta"],
    ["psy_perception","Perception (hallucinations/delusions)","ta"],
    ["psy_cognition","Cognition & insight","ta"],
    ["psy_risk","Suicidal / homicidal risk","ta"]] }],
  physiotherapy: [
    { title:"Referral & Chief Complaint", fields:[
      ["physio_referral","Referring doctor / diagnosis","ta"],
      ["physio_activity","Occupation / activity level","ta"]] },
    { title:"Pain Assessment", fields:[
      ["physio_vas","VAS score (0-10)"],["physio_pain_location","Location & character","ta"],
      ["physio_pain_modality","Aggravating / relieving","ta"],
      ["physio_radiation","Radiation","ta"]] },
    { title:"Postural & Gait", fields:[
      ["physio_posture","Posture assessment","ta"],
      ["physio_gait","Gait pattern","ta"],
      ["physio_deformity","Deformity / atrophy","ta"]] },
    { title:"ROM & Muscle Strength", fields:[
      ["physio_rom_active","ROM — active","ta"],["physio_rom_passive","ROM — passive","ta"],
      ["physio_strength","Muscle strength (MRC 0-5)","ta"],
      ["physio_tone","Muscle tone","ta"]] },
    { title:"Special Tests & Function", fields:[
      ["physio_special_tests","Special orthopedic/neuro tests","ta"],
      ["physio_sensation","Sensory exam","ta"],
      ["physio_reflexes","Reflexes (DTR)","ta"],
      ["physio_balance","Balance & coordination","ta"],
      ["physio_adl","Functional ability — ADL score","ta"]] },
    { title:"Physiotherapy Plan", fields:[
      ["physio_st_goals","Short-term goals","ta"],
      ["physio_lt_goals","Long-term goals","ta"],
      ["physio_modalities","Modalities (TENS/US/IFT/heat)","ta"],
      ["physio_exercises","Therapeutic exercises","ta"],
      ["physio_manual","Manual therapy","ta"],
      ["physio_home","Home advice","ta"],
      ["physio_sessions","Sessions/week & duration"]] },
  ],
};

function Field({ f, value, onChange }) {
  const [key, label, type] = f;
  const tid = `case-${key}`;
  if (type === "ta") return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">{label}</label>
      <Textarea data-testid={tid} value={value || ""} rows={2}
                onChange={(e) => onChange(key, e.target.value)}
                className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] text-sm" />
    </div>
  );
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">{label}</label>
      <Input data-testid={tid} type={type || "text"} value={value || ""}
             onChange={(e) => onChange(key, e.target.value)}
             className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono text-sm" />
    </div>
  );
}

export default function CaseTakingPanel() {
  const [data, setData] = useState({
    template: "homoeopathy", physician: "dr_tejakumar",
    complaints: [{}], prescriptions: [{}], rubrics: {},
  });
  const [caseId, setCaseId] = useState(null);
  const [cases, setCases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const loadList = useCallback(async () => {
    try { setCases((await api.caseList()).cases || []); }
    catch (err) { console.error(err); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadCase = async (id) => {
    if (!id || id === "__new__") return newCase();
    try {
      const r = await api.caseGet(id);
      setCaseId(id);
      setData({
        template: "homoeopathy", physician: "dr_tejakumar",
        complaints: [{}], prescriptions: [{}], rubrics: {},
        ...(r.data || {}),
      });
      toast.success(`Loaded: ${r.data?.name || "(unnamed)"}`);
    } catch { toast.error("Failed to load case"); }
  };
  const newCase = () => {
    setCaseId(null);
    setData({ template:"homoeopathy", physician:"dr_tejakumar",
              complaints:[{}], prescriptions:[{}], rubrics:{} });
    toast.success("New case started");
  };

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const setC = (i, k, v) => setData(d => {
    const c = [...(d.complaints || [])]; c[i] = { ...c[i], [k]: v };
    return { ...d, complaints: c };
  });
  const setRx = (i, k, v) => setData(d => {
    const r = [...(d.prescriptions || [])]; r[i] = { ...r[i], [k]: v };
    return { ...d, prescriptions: r };
  });
  const addComplaint = () => setData(d => ({ ...d, complaints: [...(d.complaints || []), {}] }));
  const removeComplaint = (i) => setData(d => ({ ...d, complaints: (d.complaints || []).filter((_, x) => x !== i) }));
  const addRx = () => setData(d => ({ ...d, prescriptions: [...(d.prescriptions || []), {}] }));
  const removeRx = (i) => setData(d => ({ ...d, prescriptions: (d.prescriptions || []).filter((_, x) => x !== i) }));

  const toggleRubric = (group, item) => setData(d => {
    const rubrics = { ...(d.rubrics || {}) };
    const cur = rubrics[group] || [];
    rubrics[group] = cur.includes(item) ? cur.filter(x => x !== item) : [...cur, item];
    return { ...d, rubrics };
  });
  const isTicked = (group, item) => (data.rubrics?.[group] || []).includes(item);

  const save = async () => {
    if (!data.name || !data.name.trim()) { toast.error("Please enter patient name"); return; }
    setSaving(true);
    try {
      const r = await api.caseSave({ case_id: caseId, data });
      setCaseId(r.case_id); setSavedAt(r.updated_at);
      toast.success("Case saved"); loadList();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(api.casePdfUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data }),
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PRANA_case_${(data.name || "case").replace(/\s+/g, "_")}.pdf`;
      link.click(); URL.revokeObjectURL(link.href);
    } catch { toast.error("PDF failed"); }
    setDownloading(false);
  };

  const sections = data.template === "homoeopathy"
    ? HOMEO_SECTIONS
    : [...ALLO_COMMON, ...(ALLO_TEMPLATE_SECTIONS[data.template] || [])];

  return (
    <div className="space-y-6" data-testid="case-taking-panel">
      {/* Sticky action bar */}
      <div className="sticky top-16 z-20 flex flex-wrap items-center gap-3 bg-[#0F1115]/95 backdrop-blur-md
                       border border-[#2A2D35] px-4 py-3">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[9px] uppercase tracking-widest text-[#A19E95] font-mono mb-1">Open Case</div>
          <Select value={caseId || "__new__"} onValueChange={loadCase}>
            <SelectTrigger data-testid="case-picker" className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] max-w-md">
              <SelectValue placeholder="New case or pick saved…" />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-72">
              <SelectItem value="__new__">+ New Case</SelectItem>
              {cases.map(c => (
                <SelectItem key={c.case_id} value={c.case_id}>
                  {c.patient_name}
                  <span className="text-[#6E6B64] text-xs ml-2 font-mono">
                    {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ""}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="case-new-btn" onClick={newCase} variant="ghost"
                className="text-[#A19E95] hover:text-[#D4AF37] border border-[#2A2D35] rounded-sm">
          <FilePlus size={14} className="mr-2" /> New
        </Button>
        <Button data-testid="case-save-btn" onClick={save} disabled={saving}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>
                  : <><Save size={14} className="mr-2" /> Save</>}
        </Button>
        <Button data-testid="case-pdf-btn" onClick={download} disabled={downloading}
                variant="ghost"
                className="text-[#A19E95] hover:text-[#D4AF37] border border-[#2A2D35] rounded-sm">
          {downloading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Building…</>
                       : <><Download size={14} className="mr-2" /> Download PDF</>}
        </Button>
        {savedAt && <span className="text-[10px] text-[#6E6B64] font-mono">Saved {new Date(savedAt).toLocaleTimeString()}</span>}
      </div>

      {/* Clinic banner */}
      <div className="border-2 border-[#8B3A1F]/40 bg-[#181A1F] p-4 text-center">
        <div className="text-2xl font-heading text-[#8B3A1F] tracking-wide">PRANA HOMOEO CARE</div>
        <div className="text-[10px] text-[#A19E95] font-mono tracking-widest mt-1">
          Homoeopathy · Allopathic Consultation · Physiotherapy · Vedic Astrology
        </div>
      </div>

      {/* Template + Physician */}
      <section className="border border-[#2A2D35] bg-[#181A1F] p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">Case Template</label>
          <Select value={data.template} onValueChange={(v) => set("template", v)}>
            <SelectTrigger data-testid="case-template" className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              {TEMPLATES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">Attending Physician</label>
          <Select value={data.physician} onValueChange={(v) => set("physician", v)}>
            <SelectTrigger data-testid="case-physician" className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              {PHYSICIANS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">Patient ID</label>
          <Input data-testid="case-patient_id" value={data.patient_id || ""} placeholder="e.g. PHC-0001"
                 onChange={(e) => set("patient_id", e.target.value)}
                 className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono" />
        </div>
      </section>

      {/* Patient info */}
      <section className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3" data-testid="case-section-personal">
        <div className="font-heading text-lg text-[#D4AF37]">1 · Patient Information</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[["name","Full Name"],["dob","Date of Birth","date"],["age","Age"],["sex","Sex"],
            ["marital_status","Marital Status"],["occupation","Occupation"],
            ["address","Address","ta"],["phone","Phone"],["email","Email"],
            ["referred_by","Referred by"],["consultation_date","Consultation Date","date"]]
            .map(f => <Field key={f[0]} f={f} value={data[f[0]]} onChange={set} />)}
        </div>
      </section>

      {/* Complaints */}
      <section className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-heading text-lg text-[#D4AF37]">2 · Main Complaints</div>
          <Button size="sm" onClick={addComplaint} data-testid="case-add-complaint" variant="ghost"
                  className="text-[#A19E95] hover:text-[#D4AF37] border border-[#2A2D35]">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
        {(data.complaints || []).map((c, i) => (
          <div key={i} className="border border-[#2A2D35] p-4 rounded-sm space-y-2">
            <div className="flex justify-between items-center">
              <div className="text-[11px] uppercase tracking-widest text-[#A8896C] font-mono">Complaint #{i + 1}</div>
              {(data.complaints || []).length > 1 && (
                <button onClick={() => removeComplaint(i)} className="text-[#C15C3D] hover:text-[#D4AF37]"
                        data-testid={`case-remove-complaint-${i}`}><Trash2 size={14} /></button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[["location","Location"],["sensation","Sensation / Nature"],
                ["onset","Onset & Duration"],["modalities","Modalities"],
                ["discharges","Discharges"],["concomitants","Concomitants"],
                ["treatment","Treatment tried"],["cause","Cause / Origin"]]
                .map(([k, label]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">{label}</label>
                    <Textarea data-testid={`case-complaint-${i}-${k}`} value={c[k] || ""} rows={2}
                              onChange={(e) => setC(i, k, e.target.value)}
                              className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] text-sm" />
                  </div>
                ))}
            </div>
          </div>
        ))}
      </section>

      {/* Adaptive sections */}
      {sections.map((s, si) => (
        <section key={si} className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3"
                 data-testid={`case-section-${si}`}>
          <div className="font-heading text-lg text-[#D4AF37]">{s.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {s.fields.map(f => <Field key={f[0]} f={f} value={data[f[0]]} onChange={set} />)}
          </div>
        </section>
      ))}

      {/* Homoeopathy Rubrics — tick & select */}
      {data.template === "homoeopathy" && (
        <section className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-4" data-testid="case-rubrics">
          <div>
            <div className="font-heading text-lg text-[#D4AF37]">10 · Rubrics — Tick & Select</div>
            <div className="text-[10px] text-[#6E6B64] font-mono">
              Selected rubrics appear as the repertorial totality in the PDF.
            </div>
          </div>
          {Object.entries(RUBRIC_GROUPS).map(([group, items]) => (
            <div key={group} className="border-t border-[#2A2D35] pt-3">
              <div className="text-[11px] uppercase tracking-widest text-[#A8896C] font-mono mb-2">{group}</div>
              <div className="flex flex-wrap gap-2">
                {items.map(item => {
                  const on = isTicked(group, item);
                  return (
                    <button key={item} onClick={() => toggleRubric(group, item)}
                            data-testid={`rubric-${item.replace(/\s+/g, "_")}`}
                            className={`text-[11px] font-mono px-2.5 py-1 rounded-sm border transition-colors ${
                              on
                                ? "bg-[#D4AF37] border-[#D4AF37] text-[#0F1115]"
                                : "bg-transparent border-[#2A2D35] text-[#A19E95] hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                            }`}>
                      {on && <Check size={10} className="inline mr-1" />}{item}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Prescription */}
      <section className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-heading text-lg text-[#D4AF37] flex items-center gap-2">
            <Pill size={18} /> Prescription
          </div>
          <Button size="sm" onClick={addRx} data-testid="case-add-rx" variant="ghost"
                  className="text-[#A19E95] hover:text-[#D4AF37] border border-[#2A2D35]">
            <Plus size={12} className="mr-1" /> Add Row
          </Button>
        </div>
        {(data.prescriptions || []).map((rx, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-2 border border-[#2A2D35] p-3 rounded-sm">
            {[["medicine","Medicine / Remedy"],["potency","Potency / Dose"],
              ["frequency","Frequency"],["duration","Duration"],
              ["instructions","Instructions"]].map(([k, label]) => (
              <div key={k} className={k === "instructions" ? "md:col-span-2" : ""}>
                <label className="text-[9px] uppercase tracking-widest text-[#A19E95] font-mono">{label}</label>
                <Input data-testid={`case-rx-${i}-${k}`} value={rx[k] || ""}
                       onChange={(e) => setRx(i, k, e.target.value)}
                       className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono text-xs" />
              </div>
            ))}
            {(data.prescriptions || []).length > 1 && (
              <button onClick={() => removeRx(i)} className="text-[#C15C3D] hover:text-[#D4AF37] self-end pb-2"
                      data-testid={`case-remove-rx-${i}`}><Trash2 size={14} /></button>
            )}
          </div>
        ))}
      </section>

      {/* Advice & follow-up */}
      <section className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-3">
        <div className="font-heading text-lg text-[#D4AF37]">Advice & Follow-Up</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[["diagnosis","Diagnosis","ta"],["investigations","Investigations recommended","ta"],
            ["notes_advice","Diet & lifestyle advice","ta"],
            ["notes_followup","Follow-up date","date"]]
            .map(f => <Field key={f[0]} f={f} value={data[f[0]]} onChange={set} />)}
        </div>
      </section>

      <div className="text-[10px] text-[#6E6B64] font-mono italic pt-2 pb-8">
        PRANA HOMOEO CARE · Confidential clinical record · Not for redistribution.
      </div>
    </div>
  );
}
